// Chunked file uploader — splits large files into ~4MB chunks so they reliably
// pass through ingress proxies that cap a single POST at ~50MB. Used primarily
// for family video uploads. Returns the assembled file_id on completion.
import { api } from "./api";

const DEFAULT_CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB — comfortably under typical 50MB ingress caps.

/**
 * Upload a File/Blob in chunks. Calls `onProgress(0..1)` as chunks complete.
 * Returns the final file_id from the backend.
 */
export async function chunkedUpload(file, { chunkSize = DEFAULT_CHUNK_SIZE, onProgress = null } = {}) {
    if (!file) throw new Error("No file supplied to chunkedUpload");
    const total_chunks = Math.max(1, Math.ceil(file.size / chunkSize));
    const init = await api.post("/files/chunked/init", {
        filename: file.name || "upload.bin",
        content_type: file.type || "application/octet-stream",
        total_chunks,
        total_size: file.size,
    });
    const uploadId = init.data.upload_id;
    let uploaded = 0;
    for (let i = 0; i < total_chunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const slice = file.slice(start, end);
        const fd = new FormData();
        fd.append("file", new File([slice], `${i}.part`, { type: file.type || "application/octet-stream" }));
        // Retry once on transient network errors.
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                await api.post(`/files/chunked/${uploadId}/chunk?index=${i}`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                break;
            } catch (err) {
                if (attempt === 1) throw err;
            }
        }
        uploaded = i + 1;
        if (onProgress) onProgress(uploaded / total_chunks);
    }
    const complete = await api.post(`/files/chunked/${uploadId}/complete`);
    return complete.data.id;
}
